import {AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {NavigationEnd, NavigationStart, RouteConfigLoadEnd, Router} from '@angular/router';
import {filter, map} from 'rxjs/operators';

import {AuthService} from './modules/shared/services/auth.service';
import {AppLoadingEventsEmitter} from './modules/shared/events/loading.events';
import {LocalisationService} from './modules/shared/services/localisation.service';
import {Languages} from './modules/shared/enums/languages';
import {UsersService} from './modules/shared/services/users.service';
import {Constants} from './modules/shared/config/constants.config';
import {AuthEventsEmitter} from './modules/shared/events/auth.events';
import {ThemeService} from './modules/shared/services/theme.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, AfterViewChecked {
    private readonly appLoadingClass: string = 'app-loading';

    @ViewChild('navbar') navbar: { wrapper: ElementRef<HTMLElement>; };
    @ViewChild('pageBodyWrapper') pageBodyWrapper: ElementRef<HTMLElement>;
    @ViewChild('mainPanel') mainPanel: ElementRef<HTMLElement>;
    @ViewChild('contentWrapper') contentWrapper: ElementRef<HTMLElement>;

    loading = true;
    tidioHasBeenStyled = false;

    constructor(
        private readonly router: Router,
        private readonly changeDetectorRef: ChangeDetectorRef,
        public readonly authService: AuthService,
        private readonly appLoadingEventsEmitter: AppLoadingEventsEmitter,
        private readonly localisationService: LocalisationService,
        private readonly usersService: UsersService,
        private readonly themeService: ThemeService,
        private readonly constants: Constants,
        private readonly authEventsEmitter: AuthEventsEmitter
    ) {
    }

    ngOnInit() {
        this.initLocalisation();
        this.initEventEmitters();

        document.body.classList.add(this.appLoadingClass);

        if (this.authService.userHasLoggedIn) {
            this.usersService.getUserProfile().subscribe(() => {
                this.changeDetectorRef.detectChanges();
            });
        }
    }

    ngAfterViewChecked() {
        this.applyTidioStyles();
    }

    urlIsAuthRoute(url: string): boolean {
        return url.startsWith('/login')
            || url.startsWith('/mobile-app')
            || url.startsWith('/register')
            || url.startsWith('/forgot-password')
            || url.startsWith('/reset/password');
    }

    urlIsWizardRoute(url: string): boolean {
        return url.startsWith('/wizard');
    }

    urlIsPricingRoute(url: string): boolean {
        return url.startsWith('/pricing') && !url.startsWith('/pricing/');
    }

    get isWizardRoute(): boolean {
        return this.urlIsWizardRoute(this.router.url);
    }

    get isPricingRoute(): boolean {
        return this.urlIsPricingRoute(this.router.url);
    }

    get completeHeader(): boolean {
        return !this.isWizardRoute && !this.isPricingRoute;
    }

    get component() {
        return this;
    }

    // initialize localisation
    private initLocalisation(lang?: string) {
        if (!lang) {
            const langQueryParamRegex = /lang=([a-zA-Z]{2})/g;
            const langQueryParam: RegExpExecArray = langQueryParamRegex.exec(window.location.href);
            let language;

            // SET FROM LANG PARAM
            if (langQueryParam) {
                language = langQueryParam[1].toUpperCase();

                if (this.authService.userHasLoggedIn) {
                    setTimeout(() => {
                        this.usersService
                            .updateUserLang((language === 'BG' ? Languages.Bulgarian : Languages.English))
                            .subscribe(() => {
                                console.log('lang updated');
                            });
                    }, 1);
                }

                this.localisationService.setAutoLanguage();

                return this.localisationService.useLanguage((language === 'BG' ? Languages.Bulgarian : Languages.English));
            }

            // AUTO DETECT USER
            if (!this.localisationService.getAutoLanguage()) {
                this.localisationService.getUserIpData().subscribe(data => {
                    if (data['country'] && data['country'].length > 0 && (data['country'].toUpperCase() === 'BG' || data['country'].toUpperCase() === 'BULGARIA')) {
                        this.localisationService.useLanguage(Languages.Bulgarian);
                    } else {
                        this.localisationService.useLanguage(Languages.English);
                    }

                    this.localisationService.setAutoLanguage();
                }, () => {
                    // SET FROM COOKIE OR DEFAULT
                    language = this.localisationService.getLanguage();

                    if (language) {
                        this.localisationService.useLanguage(<Languages>language);
                    } else {
                        this.localisationService.useLanguage(<Languages>this.constants.defaultLanguage);
                    }

                    this.localisationService.setAutoLanguage();
                });

                return;
            }

            // SET FROM COOKIE OR DEFAULT
            language = this.localisationService.getLanguage();

            if (language) {
                this.localisationService.useLanguage(<Languages>language);
            } else {
                this.localisationService.useLanguage(<Languages>this.constants.defaultLanguage);
            }
            this.localisationService.setAutoLanguage();
        } else {
            // this.localisationService.setAutoLanguage();
            this.localisationService.useLanguage(<Languages>lang);
        }
    }

    private initEventEmitters() {
        this.authEventsEmitter.userProfileChanged$.subscribe(({locale}) => {
            this.initLocalisation(locale);
        });

        this.appLoadingEventsEmitter.appIsLoading$.subscribe(loading => {
            if (loading) {
                document.body.classList.add(this.appLoadingClass);
            } else {
                document.body.classList.remove(this.appLoadingClass);
            }

            this.loading = loading;
            this.changeDetectorRef.markForCheck();
        });

        this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
            this.changeDetectorRef.detectChanges();
            this.mainPanel.nativeElement.scrollTop = 0;
        });

        this.router.events
            .pipe(
                filter(e => e instanceof NavigationStart),
                map(e => e as NavigationStart)
            )
            .subscribe(({url}) => {
                const isRootPage: boolean = url === '/';
                const isErrorPage: boolean = url.startsWith('/error-404') || url.startsWith('/error-500');

                if (isRootPage || this.urlIsAuthRoute(url) || isErrorPage) {
                    this.mainPanel.nativeElement.classList.add('w-100');
                    this.pageBodyWrapper.nativeElement.classList.add('full-page-wrapper');

                    if (isErrorPage) {
                        this.contentWrapper.nativeElement.classList.add('p-0');
                    }
                } else {
                    this.mainPanel.nativeElement.classList.remove('w-100');
                    this.pageBodyWrapper.nativeElement.classList.remove('full-page-wrapper');
                    this.contentWrapper.nativeElement.classList.remove('p-0');
                }
            });

        this.router.events
            .pipe(
                filter(e => e instanceof RouteConfigLoadEnd && !!e.route.path)
            )
            .subscribe(() => {
                document.body.classList.remove(this.appLoadingClass);

                this.loading = false;
                this.changeDetectorRef.markForCheck();
            });
    }

    private applyTidioStyles() {
        const tidio: HTMLIFrameElement = document.querySelector('#tidio-chat-iframe');

        if (!tidio || this.tidioHasBeenStyled) {
            return;
        }

        const style: HTMLStyleElement = document.createElement('style');
        const css = `
      .flyMessage {
        display: none !important;
      }

      #button {
        #display: none;
      }

      #button #button-body {
        box-shadow: rgba(64, 29, 186, 0.5) 0px 4px 12px !important;
      }

      #button #new-message {
        top: 36px !important;
        right: 28px !important;
      }

      #button .chat-open:not(.sidebar) {
        right: 0 !important;
        top: 0 !important;
      }
    `;

        style.type = 'text/css';
        style.innerHTML = css;

        const {contentDocument: {head}} = tidio;

        head.append(style);

        this.tidioHasBeenStyled = true;
    }
}
